import { Button } from 'common/components/Button/Button'
import { DemoQuizNotice } from 'common/components/DownloadApp/DemoQuizNotice'
import { Input } from 'common/components/Forms/Inputs/Input'
import Select from 'common/components/Forms/Inputs/Select'
import { LoadingAnimation } from 'common/components/States/Loading/LoadingAnimation'
import { useTranslation } from 'next-i18next'
import React from 'react'
import { ICategories, IQuiz } from 'services/quizzes/quizzes.type'
import styled from 'styled-components'

export interface IQuizWithCategories extends IQuiz {
  categories: ICategories[]
}

interface QuizListProps {
  quizzes: IQuizWithCategories[]
  regions: { country_id: string; country_name: string }[]
  selectedRegion: string
  selectedCategory: { [quizId: string]: ICategories | null }
  error?: string | null
  loading?: boolean
  search: string
  onSearchChange: (val: string) => void
  onRegionChange: (region: string) => void
  onCategorySelect: (quiz: IQuizWithCategories, catId: string) => void
  onStartTest: (quiz: IQuizWithCategories, categoryId?: string) => void
}

export const QuizList: React.FC<QuizListProps> = ({
  quizzes,
  regions,
  selectedRegion,
  selectedCategory,
  error,
  loading,
  search,
  onSearchChange,
  onRegionChange,
  onCategorySelect,
  onStartTest,
}) => {
  const { t } = useTranslation('common')

  const filteredQuizzes = quizzes.filter((quiz) => quiz.name.toLowerCase().includes(search.toLowerCase()))
  return (
    <QuizContainer>
      <h1>{t('quiz_experience_description')}</h1>
      <RegionRow>
        <label htmlFor="region-select">Region:</label>
        <RegionSelect
          className="my-2 w-10rem"
          id="region-select"
          value={selectedRegion}
          onChange={(e) => onRegionChange(e.target.value)}
        >
          <option value="global">Global</option>
          {regions.map((region) => (
            <option key={region.country_id} value={region.country_id}>
              {region.country_name}
            </option>
          ))}
        </RegionSelect>
      </RegionRow>
      {loading && (
        <div className="flex flex-column justify-content-center align-items-center h-18rem">
          <LoadingAnimation size={56} />
        </div>
      )}
      {!loading && (
        <>
          <StyledInput placeholder="Search test..." value={search} onChange={(e) => onSearchChange(e.target.value)} />
          <DemoQuizNotice title={t('demo_quizzes_notice_title')} description={t('demo_quiz_notice_description')} />
          <QuizListWrap>
            {filteredQuizzes.map((quiz) => {
              const hasCategories = quiz.categories && quiz.categories.length > 0
              const singleCategory = quiz.categories && quiz.categories.length === 1 ? quiz.categories[0] : null
              return (
                <QuizCard key={quiz.id}>
                  <QuizTitle>{quiz.name}</QuizTitle>
                  {hasCategories && quiz.categories?.length > 1 && (
                    <StyledSelect
                      placeholder={quiz.categoriesLabel || 'Select type'}
                      value={selectedCategory[quiz.id]?.id || ''}
                      options={(quiz.categories || []).map((cat) => ({ label: cat.name, value: cat.id }))}
                      onChange={(e) => onCategorySelect(quiz, e.value)}
                    />
                  )}
                  <QuizActions>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={() => {
                        if (hasCategories) {
                          if (quiz.categories?.length === 1 && singleCategory) {
                            onStartTest(quiz, singleCategory.id)
                            return
                          }
                          if (quiz.categories?.length > 1 && !selectedCategory[quiz.id]) return
                          if (quiz.categories?.length > 1 && selectedCategory[quiz.id]) {
                            onStartTest(quiz, selectedCategory[quiz.id]!.id)
                            return
                          }
                        }
                        onStartTest(quiz)
                      }}
                      disabled={hasCategories ? quiz.categories?.length > 1 && !selectedCategory[quiz.id] : false}
                    >
                      Take Test
                    </Button>
                  </QuizActions>
                </QuizCard>
              )
            })}
          </QuizListWrap>
        </>
      )}
      {error && <ErrorMsg>{error}</ErrorMsg>}
    </QuizContainer>
  )
}

const QuizContainer = styled.div`
  max-width: 1200px;
  margin: 40px auto;
  padding: 24px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px #0001;

  @media screen and (max-width: 768px) {
    max-width: 600px;
  }
`

const RegionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`

const RegionSelect = styled.select`
  padding: 8px;
  font-size: 16px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: #f2f2f2;
`

const QuizListWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media screen and (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
`

const QuizCard = styled.div`
  height: 100%;
  background: #fafafa;
  border-radius: 16px;
  padding: 20px 16px;
  box-shadow: none;
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: box-shadow 0.2s, transform 0.2s;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`

const QuizTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #242424;
`

const QuizActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: auto;
`

const ErrorMsg = styled.div`
  color: #f47065;
  margin-top: 16px;
  text-align: center;
`

const StyledInput = styled((props: React.ComponentProps<typeof Input>) => <Input {...props} />)`
  margin-bottom: 24px;
  margin-top: 8px;
`

const StyledSelect = styled((props: React.ComponentProps<typeof Select>) => <Select {...props} />)`
  margin: 0;
`
